import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ConfettiExplosion from "react-confetti-explosion";
import { BASE_URL } from "../config";
import PagePreview from "../components/PagePreview/PagePreview";
import styles from "../styles/onboardingChat.module.css";

// Conversational business setup — the "chat" group of the signup A/B test.
// The server (/v1/onboarding/chat) drives the conversation; this page only
// renders its messages and sends the owner's answers back. Same engine and
// flow as the Admin-App chat.

const MAX_GALLERY = 8;
const RANGE = /(^|\D)(\d{1,2}(?::\d{2})?\s*[–-]\s*\d{1,2}(?::\d{2})?)(?!\d)/g;
// Keep "9:00–18:00" left-to-right inside Hebrew text
const bidi = (t) =>
  typeof t === "string" ? t.replace(RANGE, (m, pre, r) => `${pre}⁦${r}⁩`) : t;
const vibrate = (ms) => {
  try {
    navigator.vibrate && navigator.vibrate(ms);
  } catch (_) {}
};

const authHeaders = () => ({ Authorization: localStorage.getItem("auth_token") || "" });

const chatApi = async (reply) => {
  const res = reply
    ? await axios.post(`${BASE_URL}/onboarding/chat`, { reply }, { headers: authHeaders() })
    : await axios.get(`${BASE_URL}/onboarding/chat`, { headers: authHeaders() });
  if (res.data?.code !== 200) throw new Error(res.data?.message || "משהו השתבש, נסו שוב");
  return res.data;
};

// The newest bot message that asked something
const promptOf = (messages) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.from === "bot" && (m.chips || m.upload || m.input)) return m;
  }
  return null;
};

// Shrink big photos before upload (phones shoot 4000px+)
const shrink = (file, max = 1600) =>
  new Promise((resolve) => {
    if (!/^image\//.test(file.type)) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      if (scale === 1 && file.size < 1.5e6) {
        URL.revokeObjectURL(url);
        return resolve(file);
      }
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(
        (b) => {
          URL.revokeObjectURL(url);
          resolve(b ? new File([b], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }) : file);
        },
        "image/jpeg",
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

const uploadPhotos = async (form) => {
  const res = await axios.put(`${BASE_URL}/business/update_business_cover_profile`, form, {
    headers: authHeaders(),
  });
  if (res.data?.code !== 200) throw new Error("upload failed");
  return res.data.data || {};
};

/* ── message parts ── */

const Card = ({ icon, title, children }) => (
  <div className={styles.card}>
    <div className={styles.cardHead}>
      <span className={styles.cardIcon}>{icon}</span>
      <span className={styles.cardTitle}>{title}</span>
    </div>
    {children}
  </div>
);

const MessageView = ({ m, showAvatar, onShare }) => {
  if (m.from === "bot" && m.success) {
    return (
      <div className={`${styles.success} ${styles.pop}`}>
        <span className={styles.successIcon}>✓</span>
        {m.text}
      </div>
    );
  }
  if (m.from === "user") {
    return (
      <div className={`${styles.userRow} ${styles.inUser}`}>
        {!!m.images?.length && (
          <div className={styles.thumbs}>
            {m.images.map((src) => (
              <img key={src} src={src} alt="" className={styles.thumb} />
            ))}
          </div>
        )}
        {!!m.text && <div className={`${styles.bubble} ${styles.userBubble}`}>{bidi(m.text)}</div>}
      </div>
    );
  }
  const card = m.card;
  return (
    <div className={`${styles.botRow} ${styles.inBot}`}>
      {showAvatar ? (
        <img src="/assets/tor-app-icon.png" alt="" className={styles.avatar} />
      ) : (
        <span className={styles.avatarSpace} />
      )}
      <div className={`${styles.botCol} ${card ? styles.wide : ""}`}>
        {!!m.text && <div className={`${styles.bubble} ${styles.botBubble}`}>{bidi(m.text)}</div>}
        {!!m.note && <div className={styles.note}>ⓘ {m.note}</div>}
        {card?.type === "services" && (
          <Card icon="🏷️" title="השירותים">
            {(card.items || []).map((s, i) => (
              <div key={i} className={styles.row}>
                <span className={styles.rowName}>{s.name}</span>
                <span className={styles.rowMeta}>
                  {s.price == null ? "—" : `₪${s.price}`}
                  <span className={styles.dim}> · {s.minutes} דק׳</span>
                </span>
              </div>
            ))}
          </Card>
        )}
        {card?.type === "hours" && (
          <Card icon="🕘" title="שעות פעילות">
            {(card.rows || []).map((r, i) => (
              <div key={i} className={styles.row}>
                <span className={styles.rowName}>{r.label}</span>
                <span className={`${styles.rowMeta} ${r.closed ? styles.dim : ""}`}>{bidi(r.value)}</span>
              </div>
            ))}
          </Card>
        )}
        {card?.type === "description" && (
          <Card icon="📝" title="תיאור העסק">
            <p className={styles.desc}>{card.text}</p>
          </Card>
        )}
        {card?.type === "link" && (
          <Card icon="🌐" title="העמוד שלכם">
            <div className={styles.linkBox} dir="ltr">
              {card.url.replace(/^https?:\/\//, "")}
            </div>
            <div className={styles.linkBtns}>
              <button
                type="button"
                className={styles.ghostBtn}
                onClick={() => {
                  navigator.clipboard?.writeText(card.url);
                  vibrate(15);
                }}
              >
                העתקה
              </button>
              <button type="button" className={styles.ghostBtn} onClick={() => onShare(card.url)}>
                שיתוף
              </button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

const PhotoPanel = ({ value, onChange }) => {
  const [busy, setBusy] = useState(null);
  const coverInput = useRef(null);
  const galleryInput = useRef(null);

  const pickCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("cover");
    try {
      const f = await shrink(file);
      const form = new FormData();
      form.append("cover", f);
      await uploadPhotos(form);
      onChange({ ...value, cover: URL.createObjectURL(f) });
      vibrate(20);
    } catch (_) {
      alert("העלאת התמונה נכשלה, נסו שוב");
    }
    setBusy(null);
  };

  const pickGallery = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_GALLERY - value.gallery.length);
    e.target.value = "";
    if (!files.length) return;
    setBusy("gallery");
    try {
      const shrunk = await Promise.all(files.map((f) => shrink(f)));
      const form = new FormData();
      form.append("existing_images", JSON.stringify(value.galleryPaths));
      shrunk.forEach((f) => form.append("multipleImage", f));
      const data = await uploadPhotos(form);
      onChange({
        ...value,
        gallery: [...value.gallery, ...shrunk.map((f) => URL.createObjectURL(f))],
        galleryPaths: Array.isArray(data.multiImage) ? data.multiImage : value.galleryPaths,
      });
      vibrate(20);
    } catch (_) {
      alert("העלאת התמונות נכשלה, נסו שוב");
    }
    setBusy(null);
  };

  const slots = Math.min(MAX_GALLERY, Math.max(4, value.gallery.length + 1));
  return (
    <div className={styles.photoPanel}>
      <div className={styles.photoLabel}>תמונת קאבר</div>
      <button
        type="button"
        className={`${styles.cover} ${value.cover ? styles.filled : ""}`}
        onClick={() => coverInput.current?.click()}
        style={value.cover ? { backgroundImage: `url(${value.cover})` } : null}
      >
        {busy === "cover" ? (
          <span className={styles.spinner} />
        ) : value.cover ? (
          <span className={styles.replace}>החלפה</span>
        ) : (
          <>
            <span className={styles.coverIcon}>🖼️</span>
            <span className={styles.coverText}>הוספת תמונת קאבר</span>
            <span className={styles.coverHint}>התמונה הגדולה בראש העמוד שלכם</span>
          </>
        )}
      </button>
      <input ref={coverInput} type="file" accept="image/*" hidden onChange={pickCover} />

      <div className={styles.photoLabel}>גלריית תמונות</div>
      <div className={styles.grid}>
        {Array.from({ length: slots }, (_, i) => {
          const src = value.gallery[i];
          const isNext = i === value.gallery.length;
          return src ? (
            <div key={i} className={`${styles.square} ${styles.filled}`} style={{ backgroundImage: `url(${src})` }} />
          ) : (
            <button
              key={i}
              type="button"
              className={`${styles.square} ${isNext ? styles.next : ""}`}
              onClick={() => galleryInput.current?.click()}
            >
              {busy === "gallery" && isNext ? <span className={styles.spinner} /> : "+"}
            </button>
          );
        })}
      </div>
      <input ref={galleryInput} type="file" accept="image/*" multiple hidden onChange={pickGallery} />
    </div>
  );
};

/* ── page ── */

const OnboardingChat = ({ onDone, onSwitchToForm }) => {
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState(null);
  const [progress, setProgress] = useState({ current: 1, total: 8 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState({ cover: null, gallery: [], galleryPaths: [] });
  const [shown, setShown] = useState(0);
  const [boxReady, setBoxReady] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [callback, setCallback] = useState(() => (localStorage.getItem("signupCallback") ? "sent" : null));
  const instantUntil = useRef(0);
  const busyRef = useRef(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await chatApi();
      const history = res.history || [];
      const fresh = !history.some((m) => m.from === "user");
      instantUntil.current = fresh ? 0 : history.length;
      setShown(fresh ? 0 : history.length);
      setMessages(history);
      setStep(res.step);
      if (res.progress) setProgress(res.progress);
    } catch (_) {
      setLoadError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Reveal messages one by one: the owner's own reply at once, the bot's
  // after a short "typing" pause
  const revealing = shown < messages.length;
  useEffect(() => {
    if (shown > messages.length) {
      setShown(messages.length);
      return;
    }
    if (!revealing) return;
    const next = messages[shown];
    const prev = messages[shown - 1];
    const wait = next.from === "user" ? 0 : next.success ? 250 : !prev || prev.from === "user" || prev.success ? 450 : 650;
    const t = setTimeout(() => setShown((n) => n + 1), wait);
    return () => clearTimeout(t);
  }, [shown, messages, revealing]);

  // The answer box appears a moment after the question
  useEffect(() => {
    if (busy || revealing) {
      setBoxReady(false);
      return;
    }
    const t = setTimeout(() => {
      setBoxReady(true);
      vibrate(10);
    }, 300);
    return () => clearTimeout(t);
  }, [busy, revealing]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [shown, boxReady, busy]);

  const prompt = useMemo(() => promptOf(messages), [messages]);
  const last = messages[messages.length - 1];
  const awaitingConfirm = !!(last && last.from === "bot" && last.actions);
  const done = step === "done";

  const send = async (reply, echo, extra = {}) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    const localId = `local-${Date.now()}`;
    if (echo || extra.images) setMessages((m) => [...m, { id: localId, from: "user", text: echo, ...extra }]);
    let ok = true;
    try {
      const res = await chatApi(reply);
      const incoming = res.messages || [];
      setMessages((m) => [...m, ...incoming]);
      if (res.step === "done" && step !== "done") {
        vibrate([30, 60, 60]);
        setCelebrate(true);
      } else if (incoming.some((x) => x.success)) {
        vibrate(25);
      }
      setStep(res.step);
      if (res.progress) setProgress(res.progress);
    } catch (e) {
      ok = false;
      setMessages((m) => m.filter((x) => x.id !== localId));
      alert(e?.response?.data?.message || "משהו השתבש, נסו שוב");
    }
    busyRef.current = false;
    setBusy(false);
    return ok;
  };

  const submitText = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    const ok = await send({ type: "text", text: t }, t);
    if (!ok) setText(t);
    inputRef.current?.focus();
  };

  const shareLink = async (url) => {
    const message = `קובעים אצלי תור אונליין, בקלות: ${url}`;
    try {
      if (navigator.share) await navigator.share({ text: message, url });
      else {
        await navigator.clipboard?.writeText(url);
        alert("הלינק הועתק");
      }
    } catch (_) {}
  };

  // Same request as the app's "schedule a call" (email + CRM lead)
  const requestCallback = async () => {
    if (callback) return;
    setCallback("sending");
    try {
      const res = await axios.get(`${BASE_URL}/business/scheduleCall?source=signup_chat`, { headers: authHeaders() });
      if (res.data?.status !== 200 && res.data?.status !== 201) throw new Error("failed");
      vibrate([20, 40, 20]);
      setCallback("sent");
      localStorage.setItem("signupCallback", "1");
    } catch (_) {
      setCallback(null);
      alert("לא הצלחנו לשלוח את הבקשה, נסו שוב");
    }
  };

  const onChip = (chip) => {
    const v = String(chip.value || "");
    if (v === "app:callback") requestCallback();
    else if (v === "app:share") {
      const link = [...messages].reverse().find((m) => m.card?.type === "link");
      if (link) shareLink(link.card.url);
    } else if (v === "app:home" || v === "app:contacts") onDone();
    else send({ type: "chip", value: v, label: chip.label }, chip.label);
  };

  const photoCount = (photos.cover ? 1 : 0) + photos.gallery.length;
  const continuePhotos = () =>
    send({ type: "photos", count: photoCount }, null, {
      images: [photos.cover, ...photos.gallery].filter(Boolean),
    });

  const showInput = !!step && !done && step !== "photos";
  const chips = !awaitingConfirm && prompt?.chips && !busy ? prompt.chips : null;
  // Website finish: no "add clients" (that is the app's contacts screen)
  const finalActions = done && chips ? chips.filter((c) => c.value !== "app:contacts") : null;
  const plainChips = !done && step !== "photos" ? chips : null;
  const showAnswer = !!step && (!done || !!finalActions);
  const pct = done ? 100 : Math.max(0, ((progress.current - 1) / Math.max(progress.total, 1)) * 100);

  if (loading || loadError) {
    return (
      <div className={`${styles.page} ${styles.center}`}>
        {loading ? (
          <span className={styles.spinner} />
        ) : (
          <>
            <p>לא הצלחנו להתחבר כרגע</p>
            <button type="button" className={styles.primaryBtn} onClick={load}>
              נסו שוב
            </button>
            <button type="button" className={styles.textLink} onClick={onSwitchToForm}>
              להגדרה הרגילה
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.page} dir="rtl">
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.headerTitleWrap}>
            <img src="/assets/tor-app-icon.png" alt="" className={styles.headerAvatar} />
            <div>
              <div className={styles.headerTitle}>הקמת העסק</div>
              <div className={styles.headerSub}>
                {done ? "הכל מוכן 🎉" : `שלב ${progress.current} מתוך ${progress.total}`}
              </div>
            </div>
          </div>
          {!done && step && (
            <button type="button" className={styles.headerPill} disabled={busy} onClick={onSwitchToForm}>
              להגדרה הרגילה
            </button>
          )}
        </div>
        <div className={styles.track}>
          <div className={styles.fill} style={{ width: `${pct}%` }} />
        </div>
      </header>

      <main className={styles.list}>
        {messages.slice(0, shown).map((m, i) => {
          const prev = messages[i - 1];
          const showAvatar = m.from === "bot" && (!prev || prev.from !== "bot" || prev.success);
          const anim = i >= instantUntil.current;
          return (
            <div key={`${m.id}-${i}`} className={anim ? "" : styles.noAnim}>
              <MessageView m={m} showAvatar={showAvatar} onShare={shareLink} />
              {step === "photos" && m === prompt && m.upload && !busy && (
                <div className={styles.photoWrap}>
                  <PhotoPanel value={photos} onChange={setPhotos} />
                </div>
              )}
            </div>
          );
        })}
        {done && !revealing && shown >= messages.length && <PagePreview src="chat" compact />}
        {(busy || (revealing && messages[shown]?.from === "bot")) && (
          <div className={styles.botRow}>
            <img src="/assets/tor-app-icon.png" alt="" className={styles.avatar} />
            <div className={`${styles.bubble} ${styles.botBubble} ${styles.typing}`}>
              <i />
              <i />
              <i />
            </div>
          </div>
        )}

        {showAnswer && (
          <div className={`${styles.answerBox} ${boxReady ? styles.answerIn : styles.answerOut}`}>
            {awaitingConfirm && !busy && (
              <div className={styles.actions}>
                <button type="button" className={styles.primaryBtn} onClick={() => send({ type: "confirm" }, "נכון")}>
                  ✓ נכון, לשמור
                </button>
                <button type="button" className={styles.secondaryBtn} onClick={() => send({ type: "edit" }, "לתקן")}>
                  לתקן
                </button>
              </div>
            )}

            {step === "photos" && !awaitingConfirm && !busy && (
              <div className={styles.actions}>
                <button type="button" className={styles.primaryBtn} disabled={!photoCount} onClick={continuePhotos}>
                  {photoCount === 1 ? "המשך (תמונה אחת)" : photoCount ? `המשך (${photoCount} תמונות)` : "המשך"}
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => onChip(prompt?.chips?.[0] || { label: "אחר כך", value: "skip" })}
                >
                  אחר כך
                </button>
              </div>
            )}

            {!!finalActions && (
              <div className={styles.finalList}>
                {finalActions.map((c) =>
                  c.value === "app:callback" ? (
                    callback === "sent" ? (
                      <div key={c.value} className={styles.callbackSent}>
                        ✓ קיבלנו! נחזור אליכם בהקדם ❤️
                      </div>
                    ) : (
                      <button
                        key={c.value}
                        type="button"
                        className={styles.callbackBtn}
                        disabled={callback === "sending"}
                        onClick={() => onChip(c)}
                      >
                        {callback === "sending" ? <span className={styles.spinnerLight} /> : <>📞 {c.label}</>}
                      </button>
                    )
                  ) : (
                    <button key={c.value} type="button" className={styles.secondaryBtn} onClick={() => onChip(c)}>
                      {c.value === "app:home" ? "סיימתי, להמשך" : c.label}
                    </button>
                  )
                )}
              </div>
            )}

            {!!plainChips?.length && (
              <div className={styles.chips}>
                {plainChips.map((c) => (
                  <button
                    key={`${c.value}-${c.label}`}
                    type="button"
                    className={`${styles.chip} ${c.value === "skip" ? styles.chipQuiet : ""}`}
                    onClick={() => onChip(c)}
                  >
                    {bidi(c.label)}
                  </button>
                ))}
              </div>
            )}

            {showInput && (
              <form className={styles.inputRow} onSubmit={submitText}>
                <input
                  ref={inputRef}
                  className={styles.input}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={prompt?.input?.placeholder || "כתבו כאן…"}
                  maxLength={1000}
                  dir="rtl"
                />
                <button type="submit" className={styles.sendBtn} disabled={!text.trim() || busy} aria-label="שליחה">
                  ↑
                </button>
              </form>
            )}
          </div>
        )}
        <div ref={bottomRef} />
      </main>

      {celebrate && (
        <div className={styles.confetti}>
          <ConfettiExplosion force={0.8} duration={3200} particleCount={160} width={1400} onComplete={() => setCelebrate(false)} />
        </div>
      )}
    </div>
  );
};

export default OnboardingChat;
